/**
 * Resolve and format credit report dates for display.
 * Separates provider/bureau report date from FundElite import/saved date.
 */

const BUREAU_NAME_TO_KEY = {
  transunion: 'transunion',
  experian: 'experian',
  equifax: 'equifax',
};

export const normalizeBureauKey = (bureauName) => {
  const raw = String(bureauName || '').toLowerCase().replace(/\s+/g, '');
  return BUREAU_NAME_TO_KEY[raw] || raw;
};

const isPresent = (value) => value !== null && value !== undefined && value !== '';

/** Parse ISO strings, Date objects, and common US date strings. */
export const parseCreditReportDate = (value) => {
  if (!isPresent(value)) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const text = String(value).trim();
  if (!text) return null;

  const slashMatch = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (slashMatch) {
    const [, mm, dd, yy] = slashMatch;
    const year = yy.length === 2 ? `20${yy}` : yy;
    const parsed = new Date(`${year}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}T12:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  // ISO date-only and midnight UTC timestamps — use local noon to avoid TZ day rollback.
  const isoDateMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?)?$/);
  if (isoDateMatch) {
    const [, year, mm, dd] = isoDateMatch;
    const parsed = new Date(`${year}-${mm}-${dd}T12:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const isoDateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDateOnly) {
    const [, year, mm, dd] = isoDateOnly;
    const parsed = new Date(`${year}-${mm}-${dd}T12:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const pickFirstCreditReportDate = (...values) => {
  for (const value of values) {
    const parsed = parseCreditReportDate(value);
    if (parsed) return parsed;
  }
  return null;
};

const getParsedData = (report) => report?.parsed_data || null;

const getParsedBureauData = (report, bureauKey) => {
  const parsed = getParsedData(report);
  if (!parsed || !bureauKey) return null;
  return parsed?.bureaus?.[bureauKey] || null;
};

/**
 * Report-level provider date precedence (never uses import timestamps).
 * 1. parsed_data.report_date
 * 2. parsed_data.reportDate
 * 3. import_metadata.report_date
 * 4. top-level report.report_date / reportDate
 */
export const resolveReportDateRaw = (report) => {
  if (!report) return null;

  const parsed = getParsedData(report);

  return pickFirstCreditReportDate(
    parsed?.report_date,
    parsed?.reportDate,
    report.import_metadata?.report_date,
    report.import_metadata?.reportDate,
    report.report_date,
    report.reportDate,
  );
};

/**
 * Bureau card provider date precedence.
 * Never use stale top-level bureau.report_date when parsed/import metadata exists.
 */
export const resolveBureauReportDateRaw = (report, bureauName) => {
  if (!report) return null;

  const bureauKey = normalizeBureauKey(bureauName);
  const parsed = getParsedData(report);
  const parsedBureau = getParsedBureauData(report, bureauKey);

  const fromParsed = pickFirstCreditReportDate(
    parsedBureau?.report_date,
    parsedBureau?.reportDate,
    parsed?.report_date,
    parsed?.reportDate,
  );
  if (fromParsed) return fromParsed;

  return pickFirstCreditReportDate(
    report.import_metadata?.report_date,
    report.import_metadata?.reportDate,
    report.report_date,
    report.reportDate,
  );
};

/** SaaS import timestamp only — not provider report date fields. */
export const resolveImportedToFundEliteRaw = (report) => {
  if (!report) return null;

  return pickFirstCreditReportDate(
    report.imported_at,
    report.import_metadata?.imported_at,
    report.last_imported_at,
    report.created_at,
  );
};

/** MM/DD/YYYY — provider report date */
export const formatReportDateLong = (value) => {
  const parsed = parseCreditReportDate(value);
  if (!parsed) return null;
  return parsed.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

/** MM/DD/YY — FundElite import date */
export const formatImportedToFundEliteDate = (value) => {
  const parsed = parseCreditReportDate(value);
  if (!parsed) return null;
  return parsed.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  });
};

export const buildCreditReportDateLabels = (report, { bureauName = null, t } = {}) => {
  const reportDateRaw = bureauName
    ? resolveBureauReportDateRaw(report, bureauName)
    : resolveReportDateRaw(report);
  const importedRaw = resolveImportedToFundEliteRaw(report);

  const notAvailable = t?.('creditReport.notAvailable') || 'N/A';
  const reportDateLabel = t?.('creditReport.reportDate') || 'Report Date';
  const importedLabel = t?.('creditReport.importedToFundElite') || 'Imported to FundElite';

  const reportDateFormatted = reportDateRaw ? formatReportDateLong(reportDateRaw) : null;
  const importedFormatted = importedRaw ? formatImportedToFundEliteDate(importedRaw) : null;

  return {
    reportDateRaw,
    importedRaw,
    reportDateFormatted,
    importedFormatted,
    reportDateLine: `${reportDateLabel}: ${reportDateFormatted || notAvailable}`,
    importedLine: importedFormatted ? `${importedLabel}: ${importedFormatted}` : null,
  };
};
